import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

export type EquipmentStatus = 'not_configured' | 'in_progress' | 'configured' | 'validating';
export type ProposalStatus = 'draft' | 'validated';
export type SyncStatus = 'synced' | 'pending' | 'failed';
export type AssessmentType = 'influent' | 'effluent';

export interface Proposal {
  id: string;
  proposalNumber: string;    // Full display number: AU-PRO-2026-00001-R0
  baseNumber: string;        // Without revision: AU-PRO-2026-00001
  revisionNumber: number;    // 0 = original, 1,2,3… = revisions
  title: string;
  description?: string;
  customerId: string | null;
  customerName: string | null;
  status: ProposalStatus;
  isLocalOnly: boolean;
  savedToDb: boolean;
  syncStatus: SyncStatus;
  countryCode?: string;
  standard?: string;         // WSAA/AWA, UKWIR/WIMES, AWWA, ISO
  equipmentList?: any[];
  processTrainIds?: string[];
  sampleIds?: string[];
  notes?: string;
  revisionReason?: string;   // Mandatory reason captured when creating a revision
  isImmutable?: boolean;     // True once a proposal is validated — blocks edits/delete
  createdAt: string;
  updatedAt: string;
  dbId?: number;
}

export interface ActivityEntry {
  id: string;
  customerId: string | null;
  proposalId: string | null;
  action: string;
  page: string;
  details?: Record<string, unknown>;
  timestamp: string;
}

export interface AssessmentSession {
  type: AssessmentType;
  customerId: string | null;
  customerName: string | null;
  startedAt: string;
}

interface ProposalContextState {
  activeProposal: Proposal | null;
  proposals: Proposal[];
  assessmentSession: AssessmentSession | null;
  equipmentStatusMap: Record<string, EquipmentStatus>;
  activityLog: ActivityEntry[];

  enterProposalMode: (proposal: Proposal) => void;
  exitProposalMode: () => void;
  updateActiveProposal: (updates: Partial<Proposal>) => void;
  saveProposal: (data: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt'>) => Proposal;
  updateProposal: (id: string, updates: Partial<Proposal>) => void;
  deleteProposal: (id: string) => void;
  markProposalSavedToDb: (id: string, dbId: number) => void;
  setSyncStatus: (id: string, status: SyncStatus) => void;

  startAssessmentSession: (type: AssessmentType, customerId: string | null, customerName: string | null) => void;
  endAssessmentSession: () => void;

  updateEquipmentStatus: (key: string, status: EquipmentStatus) => void;
  logActivity: (action: string, page: string, customerId?: string | null, proposalId?: string | null, details?: Record<string, unknown>) => void;

  // Number generation (peek — no side effect)
  previewProposalNumber: (countryCode: string | null | undefined, isLocalOnly: boolean) => string;
  // Number generation (consume — increments seq counter)
  consumeProposalNumber: (countryCode: string | null | undefined) => string;
}

// ─── Storage Keys ─────────────────────────────────────────────────────────────
const LS_PROPOSALS_KEY      = 'rb_proposals_v2';
const LS_ACTIVITY_KEY       = 'rb_activity_log_v1';
const LS_ACTIVE_PROPOSAL_KEY = 'rb_active_proposal_v1';
const LS_ASSESSMENT_KEY     = 'rb_assessment_session_v1';
const LS_SEQ_KEY            = 'rb_proposal_seq_v1';  // { "2026": 3 }

// ─── Industry-Standard Proposal Numbering ────────────────────────────────────
//  AU:  AU-PRO-{YYYY}-{NNNNN}-R{N}           WSAA / AWA
//  GB:  UK-PROP/{YYYY}/{NNNNN}/R{N}           UKWIR / WIMES
//  US:  US-AWWA-{YYYY}-{NNNNN}-REV{N}        AWWA
//  INT: INT-PRO-{YYYY}-{NNNNN}-R{N}           ISO (default)

export const COUNTRY_STANDARD: Record<string, string> = {
  AU: 'WSAA/AWA', GB: 'UKWIR/WIMES', UK_EA: 'UKWIR/WIMES',
  US: 'AWWA', CA: 'ISO', NZ: 'ISO', IE: 'ISO',
  DE: 'ISO', FR: 'ISO', SG: 'ISO', OTHER: 'ISO', INT: 'ISO',
  PL: 'PN-EN/Wody Polskie', PL_WP: 'PN-EN/Wody Polskie',
};

export function buildProposalBaseNumber(countryCode: string | null | undefined, seqNum: number): string {
  const year = new Date().getFullYear();
  const seq = String(seqNum).padStart(5, '0');
  const c = countryCode?.toUpperCase();
  if (c === 'AU')               return `AU-PRO-${year}-${seq}`;
  if (c === 'GB' || c === 'UK_EA') return `UK-PROP/${year}/${seq}`;
  if (c === 'US')               return `US-AWWA-${year}-${seq}`;
  if (c === 'PL' || c === 'PL_WP') return `PL-PROP/${year}/${seq}`;
  return `INT-PRO-${year}-${seq}`;
}

export function buildProposalNumber(baseNumber: string | null | undefined, revNum: number, countryCode?: string | null): string {
  const c = countryCode?.toUpperCase();
  // Guard against legacy proposals / callers that pass an undefined base number.
  const base = baseNumber || buildProposalBaseNumber(countryCode, peekNextSeq(new Date().getFullYear()));
  if (c === 'GB' || c === 'UK_EA' || base.startsWith('UK-PROP')) return `${base}/R${revNum}`;
  if (c === 'PL' || c === 'PL_WP' || base.startsWith('PL-PROP')) return `${base}/R${revNum}`;
  if (c === 'US' || base.startsWith('US-AWWA'))                  return `${base}-REV${revNum}`;
  return `${base}-R${revNum}`;
}

export function buildLocalNumber(): string {
  const hex = Date.now().toString(36).toUpperCase().slice(-5).padStart(5, '0');
  return `LOCAL-${hex}`;
}

export function incrementRevision(proposal: Proposal): string {
  // Derive a base number even when a legacy proposal has none stored: strip any
  // trailing revision suffix from the proposal number, else build a fresh base.
  const base = proposal.baseNumber
    || (proposal.proposalNumber || '').replace(/[-/]R(EV)?\d+$/i, '')
    || buildProposalBaseNumber(proposal.countryCode, peekNextSeq(new Date().getFullYear()));
  const nextRev = (proposal.revisionNumber ?? 0) + 1;
  return buildProposalNumber(base, nextRev, proposal.countryCode);
}

function peekNextSeq(year: number): number {
  try {
    const map: Record<string, number> = JSON.parse(localStorage.getItem(LS_SEQ_KEY) || '{}');
    return (map[String(year)] || 0) + 1;
  } catch { return 1; }
}

function consumeNextSeq(year: number): number {
  try {
    const map: Record<string, number> = JSON.parse(localStorage.getItem(LS_SEQ_KEY) || '{}');
    const next = (map[String(year)] || 0) + 1;
    map[String(year)] = next;
    localStorage.setItem(LS_SEQ_KEY, JSON.stringify(map));
    return next;
  } catch { return 1; }
}

// ─── Storage helpers ──────────────────────────────────────────────────────────
function loadProposals(): Proposal[] {
  try { return JSON.parse(localStorage.getItem(LS_PROPOSALS_KEY) || '[]'); } catch { return []; }
}
function saveProposalsLS(p: Proposal[]) {
  try { localStorage.setItem(LS_PROPOSALS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}
function loadActivity(): ActivityEntry[] {
  try { return JSON.parse(localStorage.getItem(LS_ACTIVITY_KEY) || '[]').slice(0, 500); } catch { return []; }
}
function saveActivityLS(log: ActivityEntry[]) {
  try { localStorage.setItem(LS_ACTIVITY_KEY, JSON.stringify(log.slice(0, 500))); } catch { /* ignore */ }
}

// ─── Context ──────────────────────────────────────────────────────────────────
const ProposalContext = createContext<ProposalContextState | null>(null);

export function ProposalProvider({ children }: { children: React.ReactNode }) {
  const [proposals, setProposals]         = useState<Proposal[]>(loadProposals);
  const [activeProposal, setActiveProposal] = useState<Proposal | null>(() => {
    try { return JSON.parse(localStorage.getItem(LS_ACTIVE_PROPOSAL_KEY) || 'null'); } catch { return null; }
  });
  const [assessmentSession, setAssessmentSession] = useState<AssessmentSession | null>(() => {
    try { return JSON.parse(localStorage.getItem(LS_ASSESSMENT_KEY) || 'null'); } catch { return null; }
  });
  const [equipmentStatusMap, setEquipmentStatusMap] = useState<Record<string, EquipmentStatus>>({});
  const [activityLog, setActivityLog]     = useState<ActivityEntry[]>(loadActivity);

  useEffect(() => { saveProposalsLS(proposals); }, [proposals]);
  useEffect(() => {
    try { localStorage.setItem(LS_ACTIVE_PROPOSAL_KEY, JSON.stringify(activeProposal)); } catch { /* ignore */ }
  }, [activeProposal]);
  useEffect(() => {
    try { localStorage.setItem(LS_ASSESSMENT_KEY, JSON.stringify(assessmentSession)); } catch { /* ignore */ }
  }, [assessmentSession]);
  useEffect(() => { saveActivityLS(activityLog); }, [activityLog]);

  const enterProposalMode = useCallback((proposal: Proposal) => {
    setActiveProposal(proposal);
    setProposals(prev => prev.find(p => p.id === proposal.id) ? prev : [proposal, ...prev]);
  }, []);

  const exitProposalMode = useCallback(() => { setActiveProposal(null); }, []);

  const updateActiveProposal = useCallback((updates: Partial<Proposal>) => {
    const now = new Date().toISOString();
    setActiveProposal(prev => prev ? { ...prev, ...updates, updatedAt: now } : null);
    setProposals(prev => prev.map(p =>
      p.id === activeProposal?.id ? { ...p, ...updates, updatedAt: now } : p
    ));
  }, [activeProposal]);

  const saveProposal = useCallback((data: Omit<Proposal, 'id' | 'createdAt' | 'updatedAt'>): Proposal => {
    const proposal: Proposal = {
      ...data,
      id: `local_${Date.now()}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setProposals(prev => [proposal, ...prev]);
    return proposal;
  }, []);

  const updateProposal = useCallback((id: string, updates: Partial<Proposal>) => {
    const now = new Date().toISOString();
    setProposals(prev => prev.map(p => p.id === id ? { ...p, ...updates, updatedAt: now } : p));
    if (activeProposal?.id === id) {
      setActiveProposal(prev => prev ? { ...prev, ...updates, updatedAt: now } : null);
    }
  }, [activeProposal]);

  const deleteProposal = useCallback((id: string) => {
    setProposals(prev => prev.filter(p => p.id !== id));
    if (activeProposal?.id === id) setActiveProposal(null);
  }, [activeProposal]);

  const markProposalSavedToDb = useCallback((id: string, dbId: number) => {
    setProposals(prev => prev.map(p =>
      p.id === id ? { ...p, savedToDb: true, dbId, syncStatus: 'synced', updatedAt: new Date().toISOString() } : p
    ));
  }, []);

  const setSyncStatus = useCallback((id: string, status: SyncStatus) => {
    setProposals(prev => prev.map(p => p.id === id ? { ...p, syncStatus: status } : p));
  }, []);

  const startAssessmentSession = useCallback((type: AssessmentType, customerId: string | null, customerName: string | null) => {
    setAssessmentSession({ type, customerId, customerName, startedAt: new Date().toISOString() });
  }, []);

  const endAssessmentSession = useCallback(() => { setAssessmentSession(null); }, []);

  const updateEquipmentStatus = useCallback((key: string, status: EquipmentStatus) => {
    setEquipmentStatusMap(prev => ({ ...prev, [key]: status }));
  }, []);

  const logActivity = useCallback((
    action: string, page: string,
    customerId?: string | null, proposalId?: string | null,
    details?: Record<string, unknown>,
  ) => {
    const entry: ActivityEntry = {
      id: `act_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      customerId: customerId ?? null,
      proposalId: proposalId ?? null,
      action, page, details,
      timestamp: new Date().toISOString(),
    };
    setActivityLog(prev => [entry, ...prev].slice(0, 500));
  }, []);

  // Preview (no seq increment) — call during UI render to show user what number will be
  const previewProposalNumber = useCallback((countryCode: string | null | undefined, isLocalOnly: boolean): string => {
    if (isLocalOnly) return `LOCAL-XXXXX`;
    const year = new Date().getFullYear();
    const seq = peekNextSeq(year);
    const base = buildProposalBaseNumber(countryCode, seq);
    return buildProposalNumber(base, 0, countryCode);
  }, []);

  // Consume (increments seq) — call once when actually saving
  const consumeProposalNumber = useCallback((countryCode: string | null | undefined): string => {
    const year = new Date().getFullYear();
    const seq = consumeNextSeq(year);
    const base = buildProposalBaseNumber(countryCode, seq);
    return buildProposalNumber(base, 0, countryCode);
  }, []);

  const value = useMemo(() => ({
    activeProposal, proposals, assessmentSession, equipmentStatusMap, activityLog,
    enterProposalMode, exitProposalMode, updateActiveProposal,
    saveProposal, updateProposal, deleteProposal, markProposalSavedToDb, setSyncStatus,
    startAssessmentSession, endAssessmentSession,
    updateEquipmentStatus, logActivity,
    previewProposalNumber, consumeProposalNumber,
  }), [
    activeProposal, proposals, assessmentSession, equipmentStatusMap, activityLog,
    enterProposalMode, exitProposalMode, updateActiveProposal,
    saveProposal, updateProposal, deleteProposal, markProposalSavedToDb, setSyncStatus,
    startAssessmentSession, endAssessmentSession,
    updateEquipmentStatus, logActivity,
    previewProposalNumber, consumeProposalNumber,
  ]);

  return <ProposalContext.Provider value={value}>{children}</ProposalContext.Provider>;
}

export function useProposal() {
  const ctx = useContext(ProposalContext);
  if (!ctx) throw new Error('useProposal must be used within ProposalProvider');
  return ctx;
}
